import { useState, useEffect, memo } from 'react';
import { collection, query, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { X, PenSquare, Trash2, Save } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface CategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  type: 'normal' | 'mercado';
  parentId: string | null;
  level: number;
}

const getLevelIcon = (level: number) => {
  switch(level) {
    case 0: return '📁';
    case 1: return '📂';
    case 2: return '📑';
    default: return '📄';
  }
};

const getLevelName = (level: number) => {
  switch(level) {
    case 0: return 'Principal';
    case 1: return 'Subcategoria';
    case 2: return 'Sub-subcategoria';
    default: return `Nível ${level}`;
  }
};

export default function CategoryModal({ isOpen, onClose }: CategoryModalProps) {
  const { currentUser } = useAuth();
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryType, setNewCategoryType] = useState<'normal' | 'mercado'>('normal');
  const [newCategoryParentId, setNewCategoryParentId] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'todos' | 'normal' | 'mercado'>('todos');

  // Filtra as categorias baseado na pesquisa e tipo
  const filteredCategories = categories.filter(category => {
    const matchesSearch = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'todos' || category.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Obter apenas categorias principais (sem parentId)
  const mainCategories = categories.filter(cat => !cat.parentId);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const categoriesQuery = query(
        collection(db, 'categories')
      );
      
      const snapshot = await getDocs(categoriesQuery);
      const categoriesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Category[];

      setCategories(categoriesData);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
      setError('Erro ao carregar as categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim() || loading || currentUser?.role !== 'admin') return;

    try {
      setLoading(true);
      setError('');

      // Verifica se já existe uma categoria com o mesmo nome
      const exists = categories.some(
        cat => cat.name.toLowerCase() === newCategoryName.trim().toLowerCase()
      );

      if (exists) {
        setError('Já existe uma categoria com este nome');
        return;
      }

      const slug = newCategoryName
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const newCategory = {
        name: newCategoryName.trim(),
        slug,
        type: newCategoryType,
        createdAt: new Date().toISOString(),
        parentId: newCategoryParentId,
        level: newCategoryParentId ? 1 : 0
      };

      const docRef = await addDoc(collection(db, 'categories'), newCategory);
      
      setCategories([...categories, { ...newCategory, id: docRef.id }]);
      setNewCategoryName('');
      setNewCategoryType('normal');
      setNewCategoryParentId(null);

    } catch (err) {
      console.error('Erro ao criar categoria:', err);
      setError('Erro ao criar a categoria');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      setLoading(true);
      setError('');

      const slug = editingCategory.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const categoryRef = doc(db, 'categories', editingCategory.id);
      
      await updateDoc(categoryRef, {
        name: editingCategory.name,
        type: editingCategory.type,
        slug,
        parentId: editingCategory.parentId || null,
        level: editingCategory.parentId ? 1 : 0,
        updatedAt: new Date().toISOString()
      });

      // Atualiza a categoria na lista local
      setCategories(categories.map(cat => 
        cat.id === editingCategory.id 
          ? { ...cat, name: editingCategory.name, type: editingCategory.type, slug, parentId: editingCategory.parentId || null, level: editingCategory.parentId ? 1 : 0 }
          : cat
      ));

      setEditingCategory(null);
    } catch (err) {
      console.error('Erro ao atualizar categoria:', err);
      setError('Erro ao atualizar a categoria. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (currentUser?.role !== 'admin') return;

    try {
      setLoading(true);
      setDeleteError(null);

      // Verifica se existem subcategorias
      const hasSubcategories = categories.some(cat => cat.parentId === categoryId);
      if (hasSubcategories) {
        setDeleteError('Não é possível excluir esta categoria pois existem subcategorias vinculadas a ela');
        return;
      }

      // Verifica se existem produtos vinculados
      const productsQuery = query(
        collection(db, 'products'),
        where('category', '==', categoryId)
      );
      const productsSnapshot = await getDocs(productsQuery);

      if (!productsSnapshot.empty) {
        setDeleteError('Não é possível excluir esta categoria pois existem produtos vinculados a ela');
        return;
      }

      await deleteDoc(doc(db, 'categories', categoryId));
      await loadCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setDeleteError('Erro ao excluir categoria');
    } finally {
      setLoading(false);
    }
  };

  const renderCategory = (category: Category) => {
    const subcategories = categories.filter(cat => cat.parentId === category.id);

    return (
      <div key={category.id} className="mb-4">
        <div className={`flex items-center gap-2 p-2 bg-white border rounded-md group
          ${category.type === 'mercado' ? 'border-orange-200' : 'border-gray-200'}
        `}>
          {editingCategory?.id === category.id ? (
            // Modo de edição
            <div className="flex-1 flex items-center gap-2">
              <input
                type="text"
                value={editingCategory.name}
                onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                className="flex-1 p-2 border rounded-md"
                placeholder="Nome da categoria"
              />
              <select
                value={editingCategory.type}
                onChange={(e) => setEditingCategory({ ...editingCategory, type: e.target.value as 'normal' | 'mercado' })}
                className="p-2 border rounded-md bg-white w-28"
              >
                <option value="normal">Normal</option>
                <option value="mercado">Mercado</option>
              </select>
              <select
                value={editingCategory.parentId || ''}
                onChange={(e) => setEditingCategory({ ...editingCategory, parentId: e.target.value || null })}
                className="p-2 border rounded-md bg-white w-40"
              >
                <option value="">Categoria Principal</option>
                {categories.filter(cat => cat.id !== editingCategory.id).map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <button
                onClick={handleUpdateCategory}
                className="p-2 hover:bg-green-50 rounded-full transition-colors"
                title="Salvar alterações"
              >
                <Save className="w-5 h-5 text-green-600" />
              </button>
              <button
                onClick={() => setEditingCategory(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Cancelar edição"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-base select-none">{getLevelIcon(category.level)}</span>
                    <span>{category.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full
                      ${category.type === 'mercado' 
                        ? 'bg-orange-100 text-orange-700' 
                        : 'bg-gray-100 text-gray-600'}
                    `}>
                      {category.type}
                    </span>
                    {category.parentId && (
                      <span className="text-xs text-gray-500">
                        (Sub de {categories.find(c => c.id === category.parentId)?.name})
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => setEditingCategory(category)}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                  title="Editar categoria"
                >
                  <PenSquare className="w-4 h-4 text-blue-600" />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  className="p-1 hover:bg-red-50 rounded-full transition-colors"
                  title="Excluir categoria"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </>
          )}
        </div>
        {/* Renderiza subcategorias */}
        {subcategories.length > 0 && (
          <div className="ml-8 mt-2">
            {subcategories.map(subcat => (
              <div key={subcat.id} className="mb-2">
                {renderCategory(subcat)}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-end">
      <div className="w-[600px] bg-white shadow-lg flex flex-col h-full">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-lg font-medium">Gerir Categorias</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 border-b space-y-4">
          {/* Filtros e pesquisa */}
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Pesquisar categorias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 p-2 border rounded-md"
            />
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as 'todos' | 'normal' | 'mercado')}
              className="p-2 border rounded-md bg-white"
            >
              <option value="todos">Todos</option>
              <option value="normal">Normal</option>
              <option value="mercado">Mercado</option>
            </select>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {mainCategories.map(category => renderCategory(category))}
        </div>

        {/* Formulário de nova categoria */}
        <div className="p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-600 mb-4">
            Total: {filteredCategories.length} {filteredCategories.length === 1 ? 'categoria' : 'categorias'}
          </div>
          
          {currentUser?.role === 'admin' && (
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Nova categoria..."
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="w-full p-2 border rounded-md"
              />
              <div className="flex gap-2">
                <select
                  value={newCategoryType}
                  onChange={(e) => setNewCategoryType(e.target.value as 'normal' | 'mercado')}
                  className="p-2 border rounded-md bg-white flex-1"
                >
                  <option value="normal">Normal</option>
                  <option value="mercado">Mercado</option>
                </select>
                <select
                  value={newCategoryParentId || ''}
                  onChange={(e) => setNewCategoryParentId(e.target.value || null)}
                  className="p-2 border rounded-md bg-white flex-1"
                >
                  <option value="">Categoria Principal</option>
                  {mainCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleCreateCategory}
                disabled={loading || !newCategoryName.trim()}
                className={`w-full px-4 py-2 rounded-md text-white
                  ${loading || !newCategoryName.trim()
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary-dark'}
                `}
              >
                Criar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}